/**
 * nodeRunState.ts — derives per-node/per-edge visual run state from a
 * `WorkflowRun`. Pure function, no React — the run's `steps` array only ever
 * carries *resolved* steps (done/skipped/failed/not_reached), so the node
 * currently executing never appears in it yet. We infer "active" as the
 * frontier: the first not-yet-resolved node whose upstream predecessors (or
 * the trigger, if it has none) have all resolved.
 */

import type {
  NodeRunState,
  WorkflowEdge,
  WorkflowNode,
  WorkflowRun,
} from "../workflow.types";

export interface RunVisualState {
  nodeStates: Record<string, NodeRunState>;
  /** Edge ids considered "taken" by the run so far (for animated styling). */
  takenEdgeIds: Set<string>;
}

export function computeRunVisualState(
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
  run: WorkflowRun | null,
): RunVisualState {
  const nodeStates: Record<string, NodeRunState> = {};
  const takenEdgeIds = new Set<string>();

  if (!run) {
    nodes.forEach((n) => {
      nodeStates[n.id] = "pending";
    });
    return { nodeStates, takenEdgeIds };
  }

  const stepByNodeId = new Map(run.steps.map((s) => [s.stepId, s]));
  const incoming = new Map<string, WorkflowEdge[]>();
  edges.forEach((e) => {
    incoming.set(e.target, [...(incoming.get(e.target) || []), e]);
  });

  let frontierAssigned = false;

  nodes.forEach((node) => {
    const step = stepByNodeId.get(node.id);
    if (step) {
      nodeStates[node.id] = step.status;
      return;
    }
    if (run.status !== "running") {
      // A finished run should have resolved every reachable node; anything
      // still missing was never reached.
      nodeStates[node.id] = "not_reached";
      return;
    }
    nodeStates[node.id] = "pending";
  });

  if (run.status === "running" && !frontierAssigned) {
    // Frontier = first pending node (in array order, i.e. left-to-right by
    // construction) whose predecessors are all already resolved.
    for (const node of nodes) {
      if (nodeStates[node.id] !== "pending") continue;
      const preds = incoming.get(node.id) || [];
      const predsResolved = preds.every((edge) => {
        const predStep = stepByNodeId.get(edge.source);
        return predStep && predStep.status !== "not_reached";
      });
      const isEntryPoint = preds.length === 0;
      if (isEntryPoint || predsResolved) {
        nodeStates[node.id] = "active";
        frontierAssigned = true;
        break;
      }
    }
  }

  edges.forEach((edge) => {
    const sourceStep = stepByNodeId.get(edge.source);
    const targetStep = stepByNodeId.get(edge.target);
    if (!sourceStep || !targetStep) return;
    if (sourceStep.status !== "done") return;
    if (sourceStep.verdict && sourceStep.verdict !== edge.sourcePort) return;
    takenEdgeIds.add(edge.id);
  });

  return { nodeStates, takenEdgeIds };
}
